import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

const FilterDropdown = ({ label, icon: Icon, options = [], selectedValues = [], onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleOption = (value) => {
        const valueKey = typeof value === 'object' ? value.value : value;
        // ensure selectedValues is an array
        const currentVals = Array.isArray(selectedValues) ? selectedValues : [];
        if (currentVals.includes(valueKey)) {
            onChange(currentVals.filter(v => v !== valueKey));
        } else {
            onChange([...currentVals, valueKey]);
        }
    };

    const clearSelection = (e) => {
        e.stopPropagation();
        onChange([]);
    };

    const isSelected = (value) => {
        const valueKey = typeof value === 'object' ? value.value : value;
        const currentVals = Array.isArray(selectedValues) ? selectedValues : [];
        return currentVals.includes(valueKey);
    };

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center justify-between gap-2 px-3 py-1.5 text-sm border rounded-lg transition-colors
                    ${(Array.isArray(selectedValues) && selectedValues.length > 0)
                        ? 'border-blue-500 text-blue-700 bg-blue-50/50'
                        : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
            >
                <div className="flex items-center gap-2">
                    {Icon && <Icon className="w-4 h-4 opacity-70" />}
                    <span className="font-medium">{label}</span>
                    {Array.isArray(selectedValues) && selectedValues.length > 0 && (
                        <span className="inline-flex items-center justify-center w-5 h-5 ml-1 text-xs font-bold text-white bg-blue-600 rounded-full">
                            {selectedValues.length}
                        </span>
                    )}
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-50 w-56 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg left-0 top-full">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</span>
                        {Array.isArray(selectedValues) && selectedValues.length > 0 && (
                            <button
                                onClick={clearSelection}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    <div className="max-h-60 overflow-y-auto p-2">
                        {(!options || options.length === 0) ? (
                            <div className="px-3 py-2 text-sm text-gray-400 italic">No options</div>
                        ) : (
                            options.map((opt, idx) => {
                                const val = typeof opt === 'object' ? opt.value : opt;
                                const display = typeof opt === 'object' ? opt.label : opt;
                                const active = isSelected(val);

                                return (
                                    <label
                                        key={idx}
                                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                                        onClick={(e) => {
                                            e.preventDefault(); // Prevents double firing if clicking label vs input
                                            toggleOption(val);
                                        }}
                                    >
                                        <div className={`flex items-center justify-center w-4 h-4 border rounded transition-colors
                                            ${active ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}
                                        >
                                            {active && <Check className="w-3 h-3 text-white" />}
                                        </div>
                                        <span className={`text-sm ${active ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                                            {display}
                                        </span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterDropdown;
